const ELEMENT_NAME = 'essentia-cart-upsell-safe';

const IGNORED_TAGS = new Set([
  'decant','decants','decants originales','essentia','promo-agosto',
  'masculino','femenino','hombre','mujer','caballero','dama','unisex',
  'decants masculinos','decants femeninos','decants unisex',
  'perfumes masculinos','perfumes femeninos',
]);

const STYLE_TAGS = new Set([
  'fresco','dulce','elegante','intenso','seductor','sensual',
  'sofisticado','limpio','versatil','premium','llamativo','acogedor',
]);

const OCCASION_TAGS = new Set([
  'dia','noche','oficina','cita','citas','fiesta','eventos','uso diario',
  'ocasiones especiales','verano','invierno','otono','primavera',
]);

const ACCORD_TAGS = new Set([
  'amaderado','ambar','ambarado','acuatico','citrico','floral','frutal',
  'afrutado','gourmand','especiado','aromatico','vainilla','cuero',
  'almizcle','jazmin','rosa','lavanda','tropical','marino','verde',
]);

function parseCsv(value) {
  return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
}

function parseTriplePipe(value) {
  return String(value || '').split('|||').map((item) => item.trim()).filter(Boolean);
}

function normalizeToken(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeSize(value) {
  return normalizeToken(value).replace(/\s+/g, '');
}

function normalizeTags(tags) {
  if (Array.isArray(tags)) return tags.map(normalizeToken).filter(Boolean);
  if (typeof tags === 'string') {
    return tags.split(',').map(normalizeToken).filter(Boolean);
  }
  return [];
}

function isFiveMl(variant) {
  return [variant?.title, variant?.public_title, variant?.option1].some((value) => {
    const normalized = normalizeSize(value);
    return normalized === '5ml' || normalized.startsWith('5ml-');
  });
}

function imageUrl(product) {
  const image = product?.featured_image;
  if (typeof image === 'string') return image;
  if (image && typeof image === 'object') return image.url || image.src || '';
  const first = Array.isArray(product?.images) ? product.images[0] : '';
  if (typeof first === 'string') return first;
  return first?.url || first?.src || '';
}

function moneyPYG(value) {
  const cents = Number(value);
  if (!Number.isFinite(cents)) return '';
  return 'Gs. ' + new Intl.NumberFormat('es-PY', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(cents / 100);
}

function recommendationsUrl(base, productId) {
  const root = window.Shopify?.routes?.root || '/';
  const source = base || root + 'recommendations/products';
  const endpoint = source.endsWith('.json') ? source : source + '.json';
  const url = new URL(endpoint, window.location.origin);
  url.searchParams.set('product_id', productId);
  url.searchParams.set('limit', '10');
  url.searchParams.set('intent', 'related');
  return url;
}

async function fetchRecommendations(base, productId) {
  const response = await fetch(recommendationsUrl(base, productId), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) throw new Error('recommendations-unavailable');
  const payload = await response.json();
  return Array.isArray(payload?.products) ? payload.products : [];
}

function productGender(product) {
  const tags = normalizeTags(product?.tags);
  const title = normalizeToken(product?.title);
  const tagBlob = tags.join('|');

  const masculine =
    tagBlob.includes('masculino') ||
    tagBlob.includes('hombre') ||
    tagBlob.includes('caballero') ||
    /\bpour homme\b/.test(title) ||
    /\bhomme\b/.test(title) ||
    /\bmen\b/.test(title);

  const feminine =
    tagBlob.includes('femenino') ||
    tagBlob.includes('mujer') ||
    tagBlob.includes('dama') ||
    /\bpour femme\b/.test(title) ||
    /\bfemme\b/.test(title) ||
    /\bwoman\b/.test(title) ||
    /\bwomen\b/.test(title);

  const unisex = tagBlob.includes('unisex') || /\bunisex\b/.test(title);

  if (masculine && feminine) return 'mixed';
  if (masculine) return 'masculine';
  if (feminine) return 'feminine';
  if (unisex) return 'unisex';
  return 'unknown';
}

function isGenderCompatible(targetGender, candidateGender) {
  if (targetGender === 'masculine') {
    return candidateGender !== 'feminine' && candidateGender !== 'mixed';
  }
  if (targetGender === 'feminine') {
    return candidateGender !== 'masculine' && candidateGender !== 'mixed';
  }
  return true;
}

function genderScore(targetGender, candidateGender) {
  if (targetGender === 'masculine') {
    if (candidateGender === 'masculine') return 30;
    if (candidateGender === 'unisex') return 9;
    if (candidateGender === 'unknown') return 1;
    return -100;
  }
  if (targetGender === 'feminine') {
    if (candidateGender === 'feminine') return 30;
    if (candidateGender === 'unisex') return 9;
    if (candidateGender === 'unknown') return 1;
    return -100;
  }
  if (targetGender === 'unisex') {
    if (candidateGender === 'unisex') return 22;
    if (candidateGender === 'unknown') return 2;
    return 0;
  }
  if (targetGender === 'mixed') {
    if (candidateGender === 'unisex' || candidateGender === 'mixed') return 7;
    return 2;
  }
  return 0;
}

function tagWeight(tag) {
  if (!tag || IGNORED_TAGS.has(tag)) return 0;
  if (tag.startsWith('vibe:')) return 12;
  if (STYLE_TAGS.has(tag)) return 8;
  if (OCCASION_TAGS.has(tag)) return 5;
  if (ACCORD_TAGS.has(tag)) return 4;
  if (tag === 'arabe' || tag === 'designer' || tag === 'nicho') return 3;
  return 1;
}

function candidateScore({ product, sourceRank, targetGender, cartTags, cartVendors }) {
  const candidateGender = productGender(product);
  if (!isGenderCompatible(targetGender, candidateGender)) return Number.NEGATIVE_INFINITY;

  const candidateTags = new Set(normalizeTags(product?.tags));
  let score = genderScore(targetGender, candidateGender);
  let meaningfulMatches = 0;

  for (const tag of cartTags) {
    if (!candidateTags.has(tag)) continue;
    const weight = tagWeight(tag);
    score += weight;
    if (weight >= 4) meaningfulMatches += 1;
  }

  if (meaningfulMatches >= 2) score += 6;
  if (meaningfulMatches >= 4) score += 4;

  const vendor = normalizeToken(product?.vendor);
  if (vendor && cartVendors.has(vendor)) score += 3;
  if (candidateTags.has('mas vendido')) score += 2;

  score += Math.max(0, 10 - Number(sourceRank || 0)) * 0.15;
  return score;
}

function buildCard(component, candidate) {
  const product = candidate.product;
  const variant = candidate.variant;

  const card = document.createElement('article');
  card.className = 'essentia-cart-upsell-safe__card';
  card.setAttribute('role', 'listitem');

  const media = document.createElement('div');
  media.className = 'essentia-cart-upsell-safe__media';

  const src = imageUrl(product);
  if (src) {
    const image = document.createElement('img');
    image.src = src;
    image.alt = product.title || '';
    image.loading = 'lazy';
    image.width = 120;
    image.height = 120;
    media.append(image);
  }

  const body = document.createElement('div');
  body.className = 'essentia-cart-upsell-safe__body';

  const title = document.createElement('p');
  title.className = 'essentia-cart-upsell-safe__title';
  const productUrl = String(product.url || '');
  if (productUrl.startsWith('/') && !productUrl.startsWith('//')) {
    const link = document.createElement('a');
    link.href = productUrl;
    link.textContent = product.title || '';
    link.setAttribute('aria-label', 'Ver detalles de ' + (product.title || 'este perfume'));
    title.append(link);
  } else {
    title.textContent = product.title || '';
  }

  const meta = document.createElement('p');
  meta.className = 'essentia-cart-upsell-safe__meta';
  meta.textContent = component.dataset.labelSize || '5 ml';

  const price = document.createElement('strong');
  price.className = 'essentia-cart-upsell-safe__price';
  price.textContent = moneyPYG(variant.price);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'essentia-cart-upsell-safe__button';
  button.dataset.safeUpsellAdd = 'true';
  button.dataset.variantId = String(variant.id || '');
  button.textContent = component.dataset.labelAdd || 'AGREGAR';
  button.setAttribute('aria-label', 'Agregar ' + (product.title || 'perfume') + ' de 5 ml al carrito');

  body.append(title, meta, price, button);
  card.append(media, body);
  return card;
}

function chooseDiverseCandidates(candidates, limit = 5) {
  const selected = [];
  const selectedIds = new Set();
  const vendorCounts = new Map();

  for (const candidate of candidates) {
    if (selected.length >= limit) break;
    const vendor = normalizeToken(candidate.product?.vendor) || 'unknown';
    const count = vendorCounts.get(vendor) || 0;
    if (count >= 2) continue;
    selected.push(candidate);
    selectedIds.add(String(candidate.product?.id || ''));
    vendorCounts.set(vendor, count + 1);
  }

  if (selected.length < limit) {
    for (const candidate of candidates) {
      if (selected.length >= limit) break;
      const id = String(candidate.product?.id || '');
      if (selectedIds.has(id)) continue;
      selected.push(candidate);
      selectedIds.add(id);
    }
  }

  return selected;
}

class EssentiaCartUpsellSafe extends HTMLElement {
  static get observedAttributes() {
    return [
      'data-reference-product-ids',
      'data-cart-product-ids',
      'data-cart-gender',
      'data-cart-tags',
      'data-cart-vendors',
    ];
  }

  connectedCallback() {
    this.refresh();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== null && oldValue !== newValue && this.isConnected) this.refresh();
  }

  async refresh() {
    const requestId = String(Date.now()) + Math.random();
    this.dataset.requestId = requestId;

    const rail = this.querySelector('[data-safe-upsell-rail]');
    if (!(rail instanceof HTMLElement)) return;

    const references = Array.from(new Set(parseCsv(this.dataset.referenceProductIds))).slice(0, 3);
    const excluded = new Set(parseCsv(this.dataset.cartProductIds));
    const targetGender = this.dataset.cartGender || 'unknown';
    const cartTags = new Set(parseTriplePipe(this.dataset.cartTags).map(normalizeToken).filter(Boolean));
    const cartVendors = new Set(parseTriplePipe(this.dataset.cartVendors).map(normalizeToken).filter(Boolean));

    if (!references.length) {
      this.hidden = true;
      return;
    }

    try {
      const results = await Promise.allSettled(
        references.map((referenceId) => fetchRecommendations(this.dataset.recommendationsUrl, referenceId))
      );

      if (this.dataset.requestId !== requestId) return;

      const byProductId = new Map();

      results.forEach((result, referenceIndex) => {
        if (result.status !== 'fulfilled') return;

        result.value.forEach((product, productIndex) => {
          const productId = String(product?.id || '');
          if (!productId || excluded.has(productId)) return;
          if (String(product?.type || '').trim().toLowerCase() !== 'decant') return;
          if (product?.available === false) return;

          const variant = Array.isArray(product?.variants)
            ? product.variants.find((item) => item?.available !== false && isFiveMl(item))
            : null;
          if (!variant) return;

          const sourceRank = referenceIndex * 10 + productIndex;
          const score = candidateScore({
            product,
            sourceRank,
            targetGender,
            cartTags,
            cartVendors,
          });
          if (!Number.isFinite(score)) return;

          const current = byProductId.get(productId);
          if (!current || score > current.score) {
            byProductId.set(productId, { product, variant, score, sourceRank });
          }
        });
      });

      if (this.dataset.requestId !== requestId) return;

      const ranked = Array.from(byProductId.values()).sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.sourceRank - b.sourceRank;
      });

      const candidates = chooseDiverseCandidates(ranked, 5);
      rail.replaceChildren(...candidates.map((candidate) => buildCard(this, candidate)));
      this.hidden = candidates.length === 0;
    } catch {
      this.hidden = true;
    }
  }

  async addVariant(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const updateCart = window.Shopify?.actions?.updateCart;
    const feedback = this.querySelector('[data-safe-upsell-feedback]');

    if (typeof updateCart !== 'function') {
      if (feedback instanceof HTMLElement) {
        feedback.textContent = this.dataset.labelError || '';
        feedback.hidden = false;
      }
      return;
    }

    const original = button.textContent;
    button.disabled = true;
    button.textContent = this.dataset.labelAdding || original;

    try {
      const result = await updateCart({
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      });

      if (Array.isArray(result?.userErrors) && result.userErrors.length) {
        throw new Error(result.userErrors[0]?.message || 'cart-update-rejected');
      }

      if (button.isConnected) button.textContent = this.dataset.labelAdded || original;
      if (feedback instanceof HTMLElement) feedback.hidden = true;
    } catch {
      if (button.isConnected) {
        button.disabled = false;
        button.textContent = original;
      }
      if (feedback instanceof HTMLElement) {
        feedback.textContent = this.dataset.labelError || '';
        feedback.hidden = false;
      }
    }
  }
}

if (!customElements.get(ELEMENT_NAME)) {
  customElements.define(ELEMENT_NAME, EssentiaCartUpsellSafe);
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('[data-safe-upsell-add]');
  if (!(button instanceof HTMLButtonElement)) return;
  const component = button.closest(ELEMENT_NAME);
  if (!(component instanceof EssentiaCartUpsellSafe)) return;
  event.preventDefault();
  component.addVariant(button);
});
